import { theme } from "@tm/utility/global";
import React, { useCallback } from "react";

import FormattedText, {
  getPlainText,
} from "../../../../components/FormattedText/FormattedText";
import { ChevronUpIcon } from "../../../../icons";
import { Card, CardPadding } from "../../Claims.style";
import { useClaims } from "../../ClaimsContext";
import { useClaimAction } from "../../modalManager";
import { ModalActionId } from "../../modalManager/types";
import type {
  ClaimCategory,
  ClaimContentType,
  ClaimInfoViewParams,
  ClaimReasonNode,
  RaiseNewClaimCardProps,
} from "../../types";
import {
  CardTitle,
  CategoryIcon,
  CategoryRow,
  CategoryTitle,
  ChevronWrapper,
  DisclaimerBox,
  PaxChevron,
  PaxTrigger,
  PaxWrapper,
  PolicyCardContainer,
  PolicyCardLeft,
  PolicyCardRight,
  PolicyCardRoute,
  PolicyCardTitle,
  PromptText,
} from "./RaiseNewClaimCard.style";

export const RaiseNewClaimCard: React.FC<RaiseNewClaimCardProps> = ({
  title,
  policyCard,
  claimPrompt,
  claimCategories = [],
  disclaimerBox,
}) => {
  const { navigateToInfo } = useClaims();
  const runClaimAction = useClaimAction();

  const handleCategoryNavigation = useCallback(
    (item: ClaimReasonNode | ClaimCategory, parentCategoryTitle?: string) => {
      if (item.isDisabled) return;

      const { contentType, detailFetchParams } = item;
      if (!contentType || !detailFetchParams) return;

      const params: ClaimInfoViewParams = {
        detailFetchParams,
        categoryTitle: getPlainText(item.title).trim() || undefined,
        categorySubtitle: parentCategoryTitle,
        categoryIcon: "icon" in item ? (item as ClaimCategory).icon : undefined,
        contentType: contentType as ClaimContentType,
      };
      navigateToInfo(params);
    },
    [navigateToInfo]
  );

  const handleCategoryClick = useCallback(
    (cat: ClaimCategory) => {
      if (cat.isDisabled) return;

      const hasReasonNodes =
        cat.reasonData?.nodes != null && cat.reasonData.nodes.length > 0;

      if (hasReasonNodes && cat.reasonData) {
        const catTitle = getPlainText(cat.title).trim() || undefined;
        runClaimAction(ModalActionId.OPEN_CLAIM_REASON, {
          extra: {
            reasonData: cat.reasonData,
            onSelectNode: (node: ClaimReasonNode) => {
              if (node.isDisabled) return;
              handleCategoryNavigation(node, catTitle);
            },
          },
        });
      } else if (cat.contentType) {
        handleCategoryNavigation(cat);
      }
    },
    [runClaimAction, handleCategoryNavigation]
  );

  const handlePaxClick = useCallback(() => {
    if (policyCard?.viewDetailData) {
      runClaimAction(ModalActionId.VIEW_TRAVELLER_DETAIL, {
        extra: { viewDetailData: policyCard.viewDetailData },
      });
    }
  }, [runClaimAction, policyCard?.viewDetailData]);

  return (
    <Card>
      <CardPadding>
        {getPlainText(title).trim() ? (
          <CardTitle>
            <FormattedText value={title} />
          </CardTitle>
        ) : null}

        {policyCard && (
          <PolicyCardContainer
            $bgColor={policyCard.style?.backgroundColor}
            $borderRadius={policyCard.style?.cornerRadius}
          >
            <PolicyCardLeft>
              <PolicyCardTitle>
                <FormattedText value={policyCard.provider} />
              </PolicyCardTitle>
              <PolicyCardRoute>
                <FormattedText value={policyCard.route} />
                {getPlainText(policyCard.travelDates).trim() ? (
                  <>
                    {" | "}
                    <FormattedText value={policyCard.travelDates} />
                  </>
                ) : null}
              </PolicyCardRoute>
            </PolicyCardLeft>

            {getPlainText(policyCard.pax).trim() ? (
              <PolicyCardRight>
                <PaxTrigger
                  onClick={handlePaxClick}
                  type="button"
                  aria-label="View traveller details"
                  disabled={!policyCard.viewDetailData}
                >
                  <PaxWrapper>
                    <FormattedText value={policyCard.pax} />
                    {policyCard.viewDetailData && (
                      <PaxChevron aria-hidden="true">
                        <ChevronUpIcon
                          color={theme.blueText}
                          cursor="pointer"
                        />
                      </PaxChevron>
                    )}
                  </PaxWrapper>
                </PaxTrigger>
              </PolicyCardRight>
            ) : null}
          </PolicyCardContainer>
        )}

        {getPlainText(claimPrompt).trim() ? (
          <PromptText>
            <FormattedText value={claimPrompt} />
          </PromptText>
        ) : null}

        {claimCategories.map((cat: ClaimCategory, index: number) => {
          const disabled = Boolean(cat.isDisabled);
          return (
            <CategoryRow
              key={cat.id ?? getPlainText(cat.title) ?? index}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              $disabled={disabled}
              onClick={() => handleCategoryClick(cat)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCategoryClick(cat);
                }
              }}
            >
              {cat.icon && <CategoryIcon src={cat.icon} alt="" />}
              <CategoryTitle $disabled={disabled}>
                <FormattedText value={cat.title} />
              </CategoryTitle>
              {cat.showChevron !== false && (
                <ChevronWrapper aria-hidden="true">
                  <ChevronUpIcon
                    color={disabled ? theme.greyText4 : theme.blueText}
                    cursor="default"
                  />
                </ChevronWrapper>
              )}
            </CategoryRow>
          );
        })}

        {getPlainText(disclaimerBox?.message).trim() ? (
          <DisclaimerBox $bgColor={disclaimerBox?.style?.backgroundColor}>
            <FormattedText value={disclaimerBox?.message} />
          </DisclaimerBox>
        ) : null}
      </CardPadding>
    </Card>
  );
};
